/** A record dock's segment switch, **Preview | Changes N** — the tour's (D6) and the
 *  operator's Pre-check-in tab's: one tab stop, ←/→ (and
 *  Home / End) move between the segments — a radio group, since the segments swap the
 *  dock's body and are not tabs with panels of their own.
 *
 *  - View mode: the dock shows Preview; Changes is unavailable (with its reason) — the
 *    draft is reviewed in edit mode.
 *  - Edit mode: entering it shows Changes when the draft already holds changes, else
 *    Preview; the first change switches to Changes; so does a save's receipt (its warnings
 *    — refused map pins — are only there) and a restored draft's banner; a Discard back to
 *    no changes returns to Preview (rather than an empty Changes side); the operator's
 *    pick holds until the next of those events. The pick lives in memory for this edit session only (never
 *    stored, never page- or tour-wide): leaving edit mode forgets it.
 *
 *  The selected segment carries a fill and weight 600, and the count rides in a navy pill
 *  ("Changes 3"), so the state never rests on colour alone. */
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import { Box, ButtonBase, Tooltip } from '@mui/material';
import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

export type DockSegment = 'preview' | 'changes';

/** The dock's segment — a small state machine over the edit session (see above). State,
 *  not derived: the operator's pick must survive renders, and only the events below move
 *  it. Adjusted during render (React's "storing information from previous renders"), so
 *  the switch lands in the same commit as the change that caused it. */
export interface DockSegmentInputs {
  editing: boolean;
  /** The draft's change count. */
  count: number;
  /** The last save's receipt (identity changes with each save; null when none). */
  receipt: unknown;
  /** The "Restored N changes" banner is showing. */
  restored: boolean;
  /** The draft's last step was a Discard (Discard all, the header's Discard). */
  discarded: boolean;
}

export function useDockSegment({ editing, count, receipt, restored, discarded }: DockSegmentInputs): {
  segment: DockSegment;
  choose: (s: DockSegment) => void;
} {
  const [segment, setSegment] = useState<DockSegment>(count > 0 || restored ? 'changes' : 'preview');
  const [seen, setSeen] = useState({ editing, count, receipt, restored });
  if (seen.editing !== editing || seen.count !== count || seen.receipt !== receipt || seen.restored !== restored) {
    let next = segment;
    // A new edit session starts from the draft, not from an earlier pick.
    if (editing && !seen.editing) next = count > 0 || restored ? 'changes' : 'preview';
    // The first change.
    else if (editing && seen.count === 0 && count > 0) next = 'changes';
    // Discarded back to nothing: nothing to review — the phone again.
    else if (editing && seen.count > 0 && count === 0 && discarded) next = 'preview';
    // What only the Changes side shows: a new receipt, a restored draft's banner.
    if (editing && receipt && receipt !== seen.receipt) next = 'changes';
    if (editing && restored && !seen.restored) next = 'changes';
    setSeen({ editing, count, receipt, restored });
    if (next !== segment) setSegment(next);
  }
  return { segment: editing ? segment : 'preview', choose: setSegment };
}

export interface DockSegmentsProps {
  segment: DockSegment;
  onChange: (s: DockSegment) => void;
  editing: boolean;
  count: number;
}

export function DockSegments({ segment, onChange, editing, count }: DockSegmentsProps) {
  const refs = useRef<Record<DockSegment, HTMLButtonElement | null>>({ preview: null, changes: null });
  const enabled: DockSegment[] = editing ? ['preview', 'changes'] : ['preview'];

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = enabled.indexOf(segment);
    let next: DockSegment | undefined;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = enabled[(i + 1) % enabled.length];
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = enabled[(i - 1 + enabled.length) % enabled.length];
    else if (e.key === 'Home') next = enabled[0];
    else if (e.key === 'End') next = enabled[enabled.length - 1];
    if (!next) return;
    e.preventDefault();
    if (next !== segment) onChange(next);
    refs.current[next]?.focus();
  };

  const item = (id: DockSegment, label: string, name: string, extra?: ReactNode) => {
    const selected = segment === id;
    const disabled = !enabled.includes(id);
    const button = (
      <ButtonBase
        ref={(el: HTMLButtonElement | null) => {
          refs.current[id] = el;
        }}
        role="radio"
        aria-checked={selected}
        aria-label={name}
        aria-disabled={disabled || undefined}
        tabIndex={selected ? 0 : -1}
        onClick={() => {
          if (!disabled && !selected) onChange(id);
        }}
        data-dock-segment={id}
        sx={{
          height: 28,
          borderRadius: '6px',
          gap: 0.75,
          px: 1.25,
          typography: 'body2',
          fontWeight: selected ? 600 : 500,
          color: disabled ? 'text.secondary' : selected ? 'primary.main' : 'text.primary',
          bgcolor: selected ? 'action.selected' : 'transparent',
          cursor: disabled ? 'default' : 'pointer',
          '&:hover': disabled || selected ? undefined : { bgcolor: 'action.hover' },
          '&.Mui-focusVisible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
        }}
      >
        {label}
        {extra}
      </ButtonBase>
    );
    // One element in both modes (an empty title shows no tooltip): E remounts nothing.
    return (
      // describeChild: the reason is the radio's description; its name stays its own.
      <Tooltip key={id} describeChild title={disabled ? 'Press E to edit — the changes are reviewed in edit mode' : ''}>
        {button}
      </Tooltip>
    );
  };

  return (
    <Box
      role="radiogroup"
      aria-label="Dock"
      onKeyDown={onKeyDown}
      data-dock-segments
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '2px',
        p: '2px',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      {item('preview', 'Preview', 'Preview', <PhoneAndroidOutlinedIcon aria-hidden sx={{ fontSize: 16, order: -1 }} />)}
      {item(
        'changes',
        'Changes',
        count > 0 ? `Changes, ${count} ${count === 1 ? 'change' : 'changes'}` : 'Changes',
        count > 0 ? (
          <Box
            component="span"
            aria-hidden
            data-dock-count
            sx={{
              minWidth: 18,
              height: 18,
              px: 0.5,
              borderRadius: 9,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontSize: 11,
              fontWeight: 600,
              lineHeight: '18px',
              textAlign: 'center',
            }}
          >
            {count}
          </Box>
        ) : null,
      )}
    </Box>
  );
}
